import {
  Form,
  Modal,
  Select,
  Steps,
  Tag,
  Typography,
  useSelect,
} from '@pankod/refine-antd'
import React from 'react'

import {
  TCertificateCategory,
  TCertificateCategoryActor,
} from '../../certificate-categories'
import { UpdateCertificateTemplateCategoryPayload } from '../types'

const { Text } = Typography

const NON_TTE_OPTION_VALUE = 0

type EditCertificateCategoryModalProps = {
  visible?: boolean
  onClose?: () => void
  onConfirm?: (
    data: UpdateCertificateTemplateCategoryPayload,
    selectedCategory: TCertificateCategory | null,
  ) => Promise<void>
  initialCertificateCategoryId?: number | string | null
}

const sortActors = (actors: TCertificateCategoryActor[] = []) =>
  [...actors].sort((left, right) => left.order - right.order)

const getActorTitle = (actor: TCertificateCategoryActor) =>
  actor.name || actor.email || actor.userId

const EditCertificateCategoryModal = ({
  visible,
  onClose,
  onConfirm,
  initialCertificateCategoryId,
}: EditCertificateCategoryModalProps) => {
  const [form] = Form.useForm<UpdateCertificateTemplateCategoryPayload>()
  const { queryResult } = useSelect<TCertificateCategory>({
    resource: 'certificate-categories',
    dataProviderName: 'lms',
    optionLabel: 'name',
    optionValue: 'id',
    queryOptions: {
      enabled: !!visible,
    },
    defaultValueQueryOptions: {
      enabled: false,
    },
    filters: [
      {
        field: 'pageSize',
        operator: 'eq',
        value: -1,
      },
    ],
  })

  React.useEffect(() => {
    if (!visible) {
      return
    }

    form.setFieldsValue({
      certificateCategoryId: Number(initialCertificateCategoryId) || null,
    })
  }, [form, initialCertificateCategoryId, visible])

  const handleCancel = () => {
    form.resetFields()
    onClose?.()
  }

  const certificateCategoryIdValue = Form.useWatch(
    'certificateCategoryId',
    form,
  )

  const categories = queryResult.data?.data || []
  const categoryOptions = [
    {
      label: 'Tanpa Kategori',
      value: NON_TTE_OPTION_VALUE,
    },
    ...categories.map((category) => ({
      label: category.name,
      value: Number(category.id),
      disabled:
        !category.isActive &&
        Number(category.id) !== Number(initialCertificateCategoryId || ''),
    })),
  ]
  const selectedCategory = categories.find(
    (category) => Number(category.id) === certificateCategoryIdValue,
  )
  const selectedActors = selectedCategory
    ? sortActors(selectedCategory.actors)
    : []

  return (
    <Modal
      title="Ubah Kategori Sertifikat"
      visible={visible}
      onCancel={handleCancel}
      onOk={async () => {
        await form.validateFields()
        const values = form.getFieldsValue() as {
          certificateCategoryId: number
        }

        await onConfirm?.(
          {
            certificateCategoryId:
              values.certificateCategoryId === NON_TTE_OPTION_VALUE
                ? null
                : Number(values.certificateCategoryId),
          },
          selectedCategory ?? null,
        )
        handleCancel()
      }}
      okText="Simpan"
      cancelText="Batal"
      confirmLoading={queryResult.isFetching}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label="Kategori Sertifikat"
          name="certificateCategoryId"
          rules={[
            { required: true, message: 'Kategori Sertifikat harus diisi' },
          ]}
        >
          <Select
            options={categoryOptions}
            placeholder="Pilih kategori sertifikat"
            allowClear
            showSearch
            loading={queryResult.isFetching}
            onSearch={() => {}}
            filterOption={(input, option) =>
              String(option?.label || '')
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          />
        </Form.Item>
        {selectedActors.length > 0 ? (
          <div className="rounded border border-neutral-200 px-4 py-3">
            <Text strong className="mb-3 block">
              Detail Approver / Signer
            </Text>
            <Steps direction="vertical" size="small" current={-1}>
              {selectedActors.map((actor) => (
                <Steps.Step
                  key={`${actor.userId}-${actor.order}`}
                  title={getActorTitle(actor)}
                  description={
                    <div className="text-xs leading-5">
                      <div>Jabatan: {actor.jabatan || '-'}</div>
                      <Tag color={actor.role === 'approver' ? 'blue' : 'green'}>
                        {actor.role === 'approver'
                          ? 'Penyetuju (Approver)'
                          : 'Penandatangan (Signer)'}
                      </Tag>
                    </div>
                  }
                />
              ))}
            </Steps>
          </div>
        ) : null}
      </Form>
    </Modal>
  )
}

export default EditCertificateCategoryModal
